// Programs-list logic shared by every surface that shows the caller's
// programs (mobile Plan tab, web-rnw console list). Pure TS — the React
// hook lives in @jackedlogs/ui (usePrograms); this module owns the
// parse + classification rules so they can't drift between apps
// (anti-monster S-B).

import {
  Program,
  ProgramProgress,
  type Program as ProgramT,
  type ProgramProgress as ProgramProgressT,
} from "./schemas/program.js";

/** Parse the GET /api/me/programs payload. Per-item zod validation:
 *  one malformed row is dropped instead of poisoning the whole list
 *  (same posture as the catalog and feedback parsers). */
export const parseProgramsList = (raw: unknown): ProgramT[] => {
  if (!Array.isArray(raw)) return [];
  const out: ProgramT[] = [];
  for (const item of raw) {
    const parsed = Program.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
};

/** Parse one GET /api/me/programs/:id/progress payload. null means
 *  "never run" (the server returns null) or schema drift. */
export const parseProgramProgress = (raw: unknown): ProgramProgressT | null => {
  if (raw === null || raw === undefined) return null;
  const parsed = ProgramProgress.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

/** Follow+Publish model (next/programs-follow-model.md, 2026-06-12):
 *  the 3 stored statuses are two orthogonal axes — lifecycle
 *  (draft → published) and follow (followed / not). UI words:
 *    - followed  (status=active):    "In corso" — the only startable bucket.
 *    - published (status=archived):  published, not followed ("Pubblicato").
 *    - drafts    (status=draft):     not followable/startable yet ("Bozza").
 *  The old split kept drafts runnable; that made drafts startable from
 *  every start surface and was part of the active/deactivated/draft
 *  confusion this model replaces. */
export const splitProgramsByStatus = (
  programs: ReadonlyArray<ProgramT>,
): { followed: ProgramT[]; published: ProgramT[]; drafts: ProgramT[] } => ({
  followed: programs.filter((p) => p.status === "active"),
  published: programs.filter((p) => p.status === "archived"),
  drafts: programs.filter((p) => p.status === "draft"),
});
